import { useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import type {
  SprintEstimateGap,
  BoardAccess,
  FlowField,
  FlowSprint,
  BoardRole,
  CanonicalStatus,
  FlowBoardDetail,
  FlowBoardSummary,
  FlowBoardTemplate,
  FlowBacklogSection,
  FlowCard,
  FlowAttachment,
  FlowCardLink,
  FlowCardSearchHit,
  FlowColumn,
  FlowComment,
  FlowEpic,
  FlowMember,
  FlowNotification,
  LinkType,
  MyCard,
  PriorityStyle,
} from "@/types/flow";

const KEY = "flow-boards";
const SECTIONS_KEY = "flow-sections";
const COMMENTS_KEY = "flow-comments";
const LINKS_KEY = "flow-links";
const EPICS_KEY = "flow-epics";

export function useFlowBoards(includeArchived = false) {
  return useQuery<FlowBoardSummary[]>({
    queryKey: [KEY, "list", includeArchived],
    queryFn: () =>
      api
        .get("/flow/boards", { params: { include_archived: includeArchived } })
        .then((r) => r.data),
  });
}

/** Saved board templates (boards an admin marked as templates). */
export function useBoardTemplates() {
  return useQuery<FlowBoardTemplate[]>({
    queryKey: [KEY, "templates"],
    queryFn: () => api.get("/flow/templates").then((r) => r.data),
  });
}

export function useFlowBoard(boardId?: number) {
  return useQuery<FlowBoardDetail>({
    queryKey: [KEY, boardId],
    queryFn: () => api.get(`/flow/boards/${boardId}`).then((r) => r.data),
    enabled: !!boardId,
    // Missing/archived boards cannot recover by retrying. Keep the normal
    // cold-boot retries for transient server and network failures.
    retry: (failureCount, error) =>
      (error as { response?: { status?: number } })?.response?.status !== 404 && failureCount < 4,
  });
}

export function useCreateBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      description?: string;
      is_private?: boolean;
      /** Setup flow: start as a draft from a template or a copy. */
      draft?: boolean;
      template?: "blank" | "dev";
      copy_from?: number;
    }) => api.post("/flow/boards", payload).then((r) => r.data as FlowBoardDetail),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdateBoard(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      name?: string;
      description?: string | null;
      is_private?: boolean;
      card_fields?: string[] | null;
      board_type?: "kanban" | "scrum";
      done_requires_uat?: boolean;
      blocked_requires_reason?: boolean;
      epic_auto_start?: boolean;
      epic_auto_complete?: boolean;
      key_prefix?: string;
      is_template?: boolean;
      priority_style?: PriorityStyle;
      auto_dev_start?: boolean;
      auto_dev_end?: boolean;
      sprint_requires_estimates?: boolean;
    }) => api.patch(`/flow/boards/${boardId}`, patch).then((r) => r.data as FlowBoardDetail),
    // Optimistic: the setting shows immediately; then only this board (and
    // the boards list) refresh, not every card on every board.
    onMutate: async (patch) => {
      const boardKey = [KEY, boardId];
      await qc.cancelQueries({ queryKey: boardKey, exact: true });
      const prev = qc.getQueryData<FlowBoardDetail>(boardKey);
      if (prev) qc.setQueryData<FlowBoardDetail>(boardKey, { ...prev, ...patch } as FlowBoardDetail);
      return { prev };
    },
    onError: (err: any, _patch, context) => {
      if (context?.prev) qc.setQueryData([KEY, boardId], context.prev);
      toast.error(err?.response?.data?.detail ?? "Couldn't update the board");
    },
    onSuccess: (_board, patch) => {
      // Becoming Scrum turns on the story fields.
      if (patch.board_type) qc.invalidateQueries({ queryKey: [KEY, boardId, "fields"] });
      if (patch.is_template !== undefined) qc.invalidateQueries({ queryKey: [KEY, "templates"] });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [KEY, boardId], exact: true });
      qc.invalidateQueries({ queryKey: [KEY, "list"] });
    },
  });
}

// --- sprints (Scrum boards) ---------------------------------------------
// Keyed under the board so live updates (which invalidate [KEY, boardId])
// refresh them too.
export function useSprints(boardId?: number, enabled = true) {
  return useQuery<FlowSprint[]>({
    queryKey: [KEY, boardId, "sprints"],
    queryFn: () => api.get(`/flow/boards/${boardId}/sprints`).then((r) => r.data),
    enabled: !!boardId && enabled,
  });
}

/** Cards that must get estimated dev dates before the sprint can start.
 *  Keyed under the board's cards so card edits refresh it. */
export function useSprintReadiness(boardId: number, sprintId: number, enabled = true) {
  return useQuery<{ missing_estimates: SprintEstimateGap[] }>({
    queryKey: [KEY, boardId, "cards", "sprint-readiness", sprintId],
    queryFn: () => api.get(`/flow/sprints/${sprintId}/readiness`).then((r) => r.data),
    enabled,
  });
}

function useSprintMutation<V>(boardId: number, fn: (v: V) => Promise<unknown>, failure: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, boardId, "sprints"] });
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? failure),
  });
}

export const useCreateSprint = (boardId: number) =>
  useSprintMutation(
    boardId,
    (v: { name?: string; goal?: string; start_date?: string | null; end_date?: string | null }) =>
      api.post(`/flow/boards/${boardId}/sprints`, v).then((r) => r.data as FlowSprint),
    "Couldn't create the sprint",
  );
export const useUpdateSprint = (boardId: number) =>
  useSprintMutation(
    boardId,
    ({ id, ...patch }: { id: number; name?: string; goal?: string | null; start_date?: string | null; end_date?: string | null }) =>
      api.patch(`/flow/sprints/${id}`, patch),
    "Couldn't update the sprint",
  );
export const useStartSprint = (boardId: number) =>
  useSprintMutation(
    boardId,
    ({ id, ...body }: { id: number; start_date: string; end_date: string; name?: string; goal?: string }) =>
      api.post(`/flow/sprints/${id}/start`, body),
    "Couldn't start the sprint",
  );
export const useCompleteSprint = (boardId: number) =>
  useSprintMutation(
    boardId,
    ({ id, move_to }: { id: number; move_to: "backlog" | "next" }) =>
      api.post(`/flow/sprints/${id}/complete`, { move_to }).then((r) => r.data),
    "Couldn't complete the sprint",
  );
export const useDeleteSprint = (boardId: number) =>
  useSprintMutation(boardId, (id: number) => api.delete(`/flow/sprints/${id}`), "Couldn't delete the sprint");

// --- board fields + publishing (owners configure) -----------------------
export function useBoardFields(boardId?: number) {
  return useQuery<FlowField[]>({
    queryKey: [KEY, boardId, "fields"],
    queryFn: () => api.get(`/flow/boards/${boardId}/fields`).then((r) => r.data),
    enabled: !!boardId,
  });
}

function useFieldMutation<V>(boardId: number, fn: (v: V) => Promise<unknown>, failure: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId, "fields"] }),
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? failure),
  });
}

export const useAddField = (boardId: number) =>
  useFieldMutation(
    boardId,
    (v: { label: string; kind: string; options?: string[]; unit?: string | null; group?: string }) =>
      api.post(`/flow/boards/${boardId}/fields`, v).then((r) => r.data as FlowField),
    "Couldn't add the field",
  );
export const useUpdateField = (boardId: number) =>
  useFieldMutation(
    boardId,
    ({ key, ...patch }: { key: string } & Partial<Omit<FlowField, "key" | "custom" | "kind">>) =>
      // card_types: null resets to the field's default types.
      api.patch(`/flow/boards/${boardId}/fields/${key}`, patch),
    "Couldn't update the field",
  );
/** Board owners: pin exactly these fields (in order) to the top of the card drawer. */
export const useSetPinnedFields = (boardId: number) =>
  useFieldMutation(
    boardId,
    (keys: string[]) => api.put(`/flow/boards/${boardId}/fields/pins`, { keys }),
    "Couldn't update the pinned fields",
  );
export const useDeleteField = (boardId: number) =>
  useFieldMutation(boardId, (key: string) => api.delete(`/flow/boards/${boardId}/fields/${key}`), "Couldn't delete the field");

export function usePublishBoard(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/flow/boards/${boardId}/publish`).then((r) => r.data as FlowBoardDetail),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? "Couldn't publish the board"),
  });
}

// --- board access (owners manage) --------------------------------------
export function useBoardAccess(boardId?: number) {
  return useQuery<BoardAccess>({
    queryKey: [KEY, boardId, "access"],
    queryFn: () => api.get(`/flow/boards/${boardId}/access`).then((r) => r.data),
    enabled: !!boardId,
  });
}

function useAccessMutation<V>(boardId: number, fn: (v: V) => Promise<{ data: BoardAccess }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.setQueryData([KEY, boardId, "access"], r.data);
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? "Couldn't update board access"),
  });
}

export const useSetBoardMember = (boardId: number) =>
  useAccessMutation(boardId, ({ userId, role }: { userId: number; role: BoardRole }) =>
    api.put(`/flow/boards/${boardId}/members/${userId}`, { role }),
  );
export const useRemoveBoardMember = (boardId: number) =>
  useAccessMutation(boardId, (userId: number) =>
    api.delete(`/flow/boards/${boardId}/members/${userId}`),
  );
export const useSetBoardTeam = (boardId: number) =>
  useAccessMutation(boardId, ({ team, role }: { team: string; role: BoardRole }) =>
    api.put(`/flow/boards/${boardId}/teams`, { team, role }),
  );
export const useRemoveBoardTeam = (boardId: number) =>
  useAccessMutation(boardId, (team: string) =>
    api.post(`/flow/boards/${boardId}/teams/remove`, { team }),
  );

export function useArchiveBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (boardId: number) => api.post(`/flow/boards/${boardId}/archive`),
    onSuccess: async (_response, boardId) => {
      // Stop in-flight reads and discard the archived board so revisiting its
      // URL cannot render cached content. Never wait for refetches to navigate.
      await qc.cancelQueries({ queryKey: [KEY, boardId] });
      qc.removeQueries({ queryKey: [KEY, boardId] });
      qc.setQueryData<FlowBoardSummary[]>([KEY, "list", false], (boards) =>
        boards?.filter((board) => board.id !== boardId),
      );
      void qc.invalidateQueries({ queryKey: [KEY, "list"] });
      void qc.invalidateQueries({ queryKey: [KEY, "templates"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? "Couldn't archive the board"),
  });
}

export function useAddColumn(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; canonical_status?: CanonicalStatus }) =>
      api
        .post(`/flow/boards/${boardId}/columns`, payload)
        .then((r) => r.data as FlowColumn),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId] }),
  });
}

export function useUpdateColumn(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: number;
      name?: string;
      canonical_status?: CanonicalStatus;
      position?: number;
    }) => api.patch(`/flow/columns/${id}`, payload).then((r) => r.data as FlowColumn),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId] }),
  });
}

export function useDeleteColumn(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (columnId: number) => api.delete(`/flow/columns/${columnId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId] }),
  });
}

export function useReorderColumns(boardId: number) {
  const qc = useQueryClient();
  const boardKey = [KEY, boardId];
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post(`/flow/boards/${boardId}/columns/reorder`, { ordered_ids: orderedIds }),
    // Optimistic, like card moves: re-position the cached columns now so
    // a reorder (board header drag or Board settings) doesn't snap back.
    onMutate: (orderedIds) => {
      qc.cancelQueries({ queryKey: boardKey, exact: true });
      const prev = qc.getQueryData<FlowBoardDetail>(boardKey);
      if (prev) {
        const pos = new Map(orderedIds.map((id, i) => [id, i]));
        qc.setQueryData<FlowBoardDetail>(boardKey, {
          ...prev,
          columns: prev.columns.map((c) => ({ ...c, position: pos.get(c.id) ?? c.position })),
        });
      }
      return { prev };
    },
    onError: (_err, _ids, context) => {
      if (context?.prev) qc.setQueryData(boardKey, context.prev);
      toast.error("Couldn't reorder columns");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: boardKey }),
  });
}

/** Live updates: subscribe to the board's server-sent event stream and
 *  refetch when anyone commits a change. Bursts (a reorder touches many
 *  rows) are coalesced into one refetch. EventSource reconnects on its
 *  own; the server sends a RESYNC after reconnecting. */
export function useBoardLiveUpdates(boardId?: number) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!boardId) return;
    const base = api.defaults.baseURL ?? "/api/v1";
    const es = new EventSource(`${base}/flow/boards/${boardId}/events`, {
      withCredentials: true,
    });
    const tables = new Set<string>();
    let timer: number | undefined;

    const flush = () => {
      timer = undefined;
      const all = tables.has("*");
      const changed = (...names: string[]) => all || names.some((n) => tables.has(n));
      // Refresh only what changed (a card move shouldn't refetch fields,
      // sprints and access).
      if (changed("flow_board", "flow_column", "flow_swimlane"))
        qc.invalidateQueries({ queryKey: [KEY, boardId], exact: true });
      if (changed("flow_card", "flow_attachment")) qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
      if (changed("flow_attachment")) qc.invalidateQueries({ queryKey: [KEY, boardId, "attachments"] });
      if (changed("flow_board_field")) qc.invalidateQueries({ queryKey: [KEY, boardId, "fields"] });
      if (changed("flow_sprint", "flow_card")) qc.invalidateQueries({ queryKey: [KEY, boardId, "sprints"] });
      if (all || tables.has("flow_epic") || tables.has("flow_card"))
        qc.invalidateQueries({ queryKey: [EPICS_KEY, boardId] });
      if (all || tables.has("flow_backlog_section"))
        qc.invalidateQueries({ queryKey: [SECTIONS_KEY, boardId] });
      if (all || tables.has("flow_comment"))
        qc.invalidateQueries({ queryKey: [COMMENTS_KEY] });
      // A linked card's status can change on any board, so card changes
      // refresh links too.
      if (all || tables.has("flow_card_link") || tables.has("flow_card"))
        qc.invalidateQueries({ queryKey: [LINKS_KEY] });
      tables.clear();
    };

    es.onmessage = (e) => {
      try {
        tables.add(JSON.parse(e.data).table ?? "*");
      } catch {
        tables.add("*");
      }
      if (timer === undefined) timer = window.setTimeout(flush, 250);
    };

    return () => {
      es.close();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [boardId, qc]);
}

// --- backlog sections -----------------------------------------------

export function useSections(boardId?: number) {
  return useQuery<FlowBacklogSection[]>({
    queryKey: [SECTIONS_KEY, boardId],
    queryFn: () => api.get(`/flow/boards/${boardId}/sections`).then((r) => r.data),
    enabled: !!boardId,
  });
}

export function useAddSection(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post(`/flow/boards/${boardId}/sections`, { name }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [SECTIONS_KEY, boardId] }),
  });
}

export function useUpdateSection(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<FlowBacklogSection>) =>
      api.patch(`/flow/sections/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [SECTIONS_KEY, boardId] }),
  });
}

/** Reorder the board's named backlog sections (the full list, in order).
 *  Optimistic: the new order shows at once; rolled back on error. */
export function useReorderSections(boardId: number) {
  const qc = useQueryClient();
  const key = [SECTIONS_KEY, boardId];
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post(`/flow/boards/${boardId}/sections/reorder`, { ordered_ids: orderedIds }).then((r) => r.data as FlowBacklogSection[]),
    onMutate: (orderedIds) => {
      qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<FlowBacklogSection[]>(key);
      if (prev) {
        const byId = new Map(prev.map((s) => [s.id, s]));
        qc.setQueryData(key, orderedIds.map((id, position) => ({ ...byId.get(id)!, position })).filter((s) => s.id != null));
      }
      return { prev };
    },
    onError: (err: any, _ids, context) => {
      if (context?.prev) qc.setQueryData(key, context.prev);
      toast.error(err?.response?.data?.detail ?? "Couldn't reorder the sections");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteSection(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: number) => api.delete(`/flow/sections/${sectionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SECTIONS_KEY, boardId] });
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
    },
  });
}

// --- cards ----------------------------------------------------------
export function useCardAttachments(boardId: number, cardId?: number) {
  return useQuery<FlowAttachment[]>({
    queryKey: [KEY, boardId, "attachments", cardId],
    queryFn: () => api.get(`/flow/cards/${cardId}/attachments`).then((r) => r.data),
    enabled: !!cardId,
  });
}

/** Path stored in rich text for an inline image (a card attachment's
 *  signed-in preview). Root-relative, so it works on every environment. */
export const cardImagePath = (cardId: number, attachmentId: number) =>
  `/api/v1/flow/cards/${cardId}/attachments/${attachmentId}/preview`;
const CARD_IMAGE_PATH = /^\/api\/v1\/flow\/cards\/\d+\/attachments\/\d+\/preview$/;
/** Only card attachments load as images (no outside links in card text). */
export const isCardImagePath = (src: string) => CARD_IMAGE_PATH.test(src);

/** Upload a pasted / dropped image as a card attachment (listed under
 *  Attachments, like Jira) and return what the editor stores. */
export function useCardImageUpload(boardId: number, cardId: number) {
  const qc = useQueryClient();
  return useCallback(
    async (file: File) => {
      const name = file.name && file.name !== "image.png" ? file.name : `screenshot-${Date.now()}.${file.type.split("/")[1] || "png"}`;
      const toastId = toast.loading(`Uploading ${name}…`);
      const body = new FormData();
      body.append("file", file, name);
      try {
        const { data } = await api.post<FlowAttachment>(`/flow/cards/${cardId}/attachments`, body, { timeout: 120_000 });
        toast.dismiss(toastId);
        void qc.invalidateQueries({ queryKey: [KEY, boardId, "attachments", cardId] });
        void qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
        return { src: cardImagePath(cardId, data.id), alt: data.filename };
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        toast.error(`${name}: ${typeof detail === "string" ? detail : "Couldn't upload the image"}`, { id: toastId });
        throw err;
      }
    },
    [qc, boardId, cardId],
  );
}

export function useFlowCards(boardId?: number) {
  return useQuery<FlowCard[]>({
    queryKey: [KEY, boardId, "cards"],
    queryFn: () => api.get(`/flow/boards/${boardId}/cards`).then((r) => r.data),
    enabled: !!boardId,
  });
}

type CardPayload = Partial<Omit<FlowCard, "id" | "board_id">> & { title: string };

export function useCreateCard(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CardPayload) =>
      api
        .post("/flow/cards", { board_id: boardId, ...payload })
        .then((r) => r.data as FlowCard),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
      qc.invalidateQueries({ queryKey: ["flow-epics", boardId] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail ?? "Couldn't create the card"),
  });
}

export function useUpdateCard(boardId: number) {
  const qc = useQueryClient();
  const cardsKey = [KEY, boardId, "cards"];
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<FlowCard>) =>
      api.patch(`/flow/cards/${id}`, patch).then((r) => r.data as FlowCard),
    // Optimistic: apply the patch to the cached list immediately so a
    // dropped card lands where it was dropped instead of snapping back
    // until the round-trip finishes. Not async — the cache must update
    // before the next paint. Rolled back on error.
    onMutate: ({ id, ...patch }) => {
      qc.cancelQueries({ queryKey: cardsKey });
      const prev = qc.getQueryData<FlowCard[]>(cardsKey);
      if (prev) {
        let next = prev.map((c) => {
          if (c.id !== id) return c;
          // custom_fields is a partial patch: merge it (null clears a key).
          if (patch.custom_fields) {
            const merged = { ...(c.custom_fields ?? {}), ...patch.custom_fields };
            for (const [k, v] of Object.entries(patch.custom_fields)) if (v == null) delete merged[k];
            return { ...c, ...patch, custom_fields: merged };
          }
          return { ...c, ...patch };
        });
        // A position is an index into the card's column: renumber that
        // column the way the server will, so a reorder doesn't flicker.
        const moved = next.find((c) => c.id === id);
        if (moved && patch.position != null) {
          const col = moved.column_id ?? null;
          const list = next
            .filter((c) => c.id !== id && (c.column_id ?? null) === col)
            .sort((a, b) => a.position - b.position || a.id - b.id);
          list.splice(Math.max(0, Math.min(patch.position, list.length)), 0, moved);
          const pos = new Map(list.map((c, i) => [c.id, i]));
          next = next.map((c) => (pos.has(c.id) ? { ...c, position: pos.get(c.id)! } : c));
        }
        qc.setQueryData<FlowCard[]>(cardsKey, next);
      }
      return { prev };
    },
    onError: (err: any, _vars, context) => {
      if (context?.prev) qc.setQueryData(cardsKey, context.prev);
      // 409 = a board rule refused the move (e.g. UAT gate) — say why.
      toast.error(err?.response?.data?.detail ?? "Couldn't update the card");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: cardsKey });
      qc.invalidateQueries({ queryKey: ["flow-epics", boardId] });
    },
  });
}

export function useLogCardTime(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hours }: { id: number; hours: number }) =>
      api.post(`/flow/cards/${id}/log-time`, { hours }).then((r) => r.data as FlowCard),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] }),
  });
}

export function useDeleteCard(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: number) => api.delete(`/flow/cards/${cardId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
      qc.invalidateQueries({ queryKey: [EPICS_KEY, boardId] });
    },
    onError: () => toast.error("Couldn't delete the card"),
  });
}

export function useArchiveCard(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: number) => api.post(`/flow/cards/${cardId}/archive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
      qc.invalidateQueries({ queryKey: ["flow-epics", boardId] });
    },
  });
}

// --- comments -------------------------------------------------------

export function useCardComments(cardId?: number) {
  return useQuery<FlowComment[]>({
    queryKey: [COMMENTS_KEY, cardId],
    queryFn: () => api.get(`/flow/cards/${cardId}/comments`).then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useAddCardComment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    // A string posts a top-level comment; { body, parent_id } posts a reply.
    mutationFn: (v: string | { body: string; parent_id?: number | null }) =>
      api
        .post(`/flow/cards/${cardId}/comments`, typeof v === "string" ? { body: v } : v)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [COMMENTS_KEY, cardId] });
      qc.invalidateQueries({ queryKey: [NOTIF_KEY] });
    },
  });
}

// --- card links -----------------------------------------------------
export function useCardLinks(cardId?: number) {
  return useQuery<FlowCardLink[]>({
    queryKey: [LINKS_KEY, cardId],
    queryFn: () => api.get(`/flow/cards/${cardId}/links`).then((r) => r.data),
    enabled: !!cardId,
  });
}

export function useAddCardLink(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { target_card_id: number; link_type: LinkType; direction: "outward" | "inward" }) =>
      api.post(`/flow/cards/${cardId}/links`, v).then((r) => r.data as FlowCardLink[]),
    onSuccess: () => qc.invalidateQueries({ queryKey: [LINKS_KEY] }),
    onError: (err) => toast.error((err as any)?.response?.data?.detail ?? "Couldn't link the cards"),
  });
}

export function useDeleteCardLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => api.delete(`/flow/links/${linkId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [LINKS_KEY] }),
    onError: (err) => toast.error((err as any)?.response?.data?.detail ?? "Couldn't remove the link"),
  });
}

/** Cards you can see, by title or #number (the link / child pickers). */
export function useCardSearch(q: string, opts: { exclude?: number; boardId?: number } = {}) {
  return useQuery<FlowCardSearchHit[]>({
    queryKey: ["flow-card-search", q, opts.exclude, opts.boardId],
    queryFn: () =>
      api
        .get("/flow/cards-search", { params: { q, exclude: opts.exclude, board_id: opts.boardId } })
        .then((r) => r.data),
    placeholderData: (prev) => prev,
  });
}

/** Edit or delete your own comment. */
export function useEditComment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => api.patch(`/flow/comments/${id}`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [COMMENTS_KEY, cardId] }),
    onError: (err) => toast.error((err as any)?.response?.data?.detail ?? "Couldn't edit the comment"),
  });
}

export function useDeleteComment(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/flow/comments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [COMMENTS_KEY, cardId] }),
    onError: (err) => toast.error((err as any)?.response?.data?.detail ?? "Couldn't delete the comment"),
  });
}

// --- swimlanes ------------------------------------------------------
export function useAddSwimlane(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; filter: Record<string, unknown> }) =>
      api.post(`/flow/boards/${boardId}/swimlanes`, payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId] }),
  });
}

export function useDeleteSwimlane(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (swimlaneId: number) => api.delete(`/flow/swimlanes/${swimlaneId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, boardId] }),
  });
}

// --- epics -----------------------------------------------------------

export function useEpics(boardId?: number) {
  return useQuery<FlowEpic[]>({
    queryKey: [EPICS_KEY, boardId],
    queryFn: () => api.get(`/flow/boards/${boardId}/epics`).then((r) => r.data),
    enabled: !!boardId,
  });
}

export function useCreateEpic(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; color?: string; description?: string }) =>
      api.post(`/flow/boards/${boardId}/epics`, payload).then((r) => r.data as FlowEpic),
    onSuccess: () => qc.invalidateQueries({ queryKey: [EPICS_KEY, boardId] }),
  });
}

export function useUpdateEpic(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<FlowEpic>) =>
      api.patch(`/flow/epics/${id}`, patch).then((r) => r.data as FlowEpic),
    onSuccess: () => qc.invalidateQueries({ queryKey: [EPICS_KEY, boardId] }),
  });
}

export function useDeleteEpic(boardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (epicId: number) => api.delete(`/flow/epics/${epicId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [EPICS_KEY, boardId] });
      qc.invalidateQueries({ queryKey: [KEY, boardId, "cards"] });
    },
  });
}

// --- members / my-work / notifications ------------------------------
const NOTIF_KEY = "flow-notifications";

export function useMembers() {
  return useQuery<FlowMember[]>({
    queryKey: ["flow-members"],
    queryFn: () => api.get("/flow/members").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyWork() {
  return useQuery<MyCard[]>({
    queryKey: ["flow-my-work"],
    queryFn: () => api.get("/flow/my-work").then((r) => r.data),
  });
}

export function useNotifications(unreadOnly = false) {
  return useQuery<FlowNotification[]>({
    queryKey: [NOTIF_KEY, "list", unreadOnly],
    queryFn: () =>
      api
        .get("/flow/notifications", { params: { unread_only: unreadOnly } })
        .then((r) => r.data),
  });
}

export function useUnreadCount() {
  return useQuery<number>({
    queryKey: [NOTIF_KEY, "count"],
    queryFn: () =>
      api.get("/flow/notifications/unread-count").then((r) => r.data.count),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/flow/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NOTIF_KEY] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/flow/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NOTIF_KEY] }),
  });
}
